import secureApi from '../../utils/secureApi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// SECURITY: CSRF token management
let csrfToken: string | null = null;

const getCsrfToken = (): string | null => {
  // In a real implementation, this would get the CSRF token from a meta tag or API
  // For now, we'll use a placeholder
  if (!csrfToken) {
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    csrfToken = metaTag?.getAttribute('content') || null;
  }
  return csrfToken;
};

// SECURITY: Enhanced secure API with CSRF protection
const secureApiWithCsrf = {
  post: async <T>(url: string, data: any): Promise<T> => {
    const token = getCsrfToken();
    const headers: any = {};
    
    if (token) {
      headers['X-CSRF-Token'] = token;
    }
    
    return secureApi.post<T>(url, data, { headers });
  },
  
  get: async <T>(url: string): Promise<T> => {
    const token = getCsrfToken();
    const headers: any = {};
    
    if (token) {
      headers['X-CSRF-Token'] = token;
    }
    
    return secureApi.get<T>(url, { headers });
  }
};

// =========================
// Payment Preview Interfaces
// =========================

export interface PaymentPreviewRequest {
  registrationId: string | null; // Optional - could be null or object id but need to be sent compulsory
  ageCategory: string;
  players: string[]; // Array of player IDs
}

export interface PaymentPreviewResponse {
  statusCode: number;
  status: string;
  message: string;
  data: {
    tournament: string;
    team: string;
    perPlayerFee: number;
    playerCount: number;
    totalAmount: number;
    fullAmount: number;
    amountPaid: number;
    minimumAmountToPay: number;
    amountToPay: number; // Add amountToPay field from API response
    balanceAmount: number;
    milestoneCompleted: string | null;
  };
}

// =========================
// Tournament Registration Interfaces
// =========================

export interface AddRegistrationRequest {
  registrationId?: string | null; // Make optional for existing registrations
  ageCategory: string;
  players: string[];
  amountPaid: number;
  paymentMode: 'online' | 'offline';
  method?: string | null; // Payment method for offline payments
}

export interface AddRegistrationResponse {
  statusCode: number;
  status: string;
  message: string;
  data: {
    registration: {
      _id: string;
      tournament: string;
      team: string;
      ageCategory: string;
      playerIds: string[];
      perPlayerFee: number;
      totalAmount: number;
      amountPaid: number;
      balanceAmount: number;
      isAmountPaidInFull: boolean;
      milestones: string[];
      milestoneDueDates: Array<{
        milestone: string;
        dueDate: string;
        totalDuePercentage: number;
      }>;
      milestonePayments: any[];
      milestoneCompleted: string | null;
      createdAt: string;
      updatedAt: string;
    };
    milestonePayment: {
      _id: string;
      tournament: string;
      team: string;
      registration: string;
      milestone: string;
      players: Array<{
        player: string;
        amount: number;
      }>;
      amount: number;
      type: string;
      paidAt: string | null;
      paymentId: string | null;
      paidStatus: string;
      createdAt: string;
      updatedAt: string;
    };
  };
}

// =========================
// Tournament Payment Order Interfaces
// =========================

export interface CreateTournamentOrderRequest {
  registration: string;  // Changed from registrationId
  milestonePaymentId: string;
  moduleType: string;    // Added moduleType
  amountPaid: number;    // Changed from amount
}

export interface CreateTournamentOrderResponse {
  statusCode: number;
  status: string;
  message: string;
  data: {
    orderId: string;
    amount: number;
    currency: string;
    receipt: string;
    keyId?: string; // Razorpay key from API response (correct field name)
  };
}

// =========================
// Payment Preview API
// =========================

/**
 * Get payment preview for tournament registration
 * @param tournamentId - The tournament ID
 * @param teamId - The team ID
 * @param requestData - Payment preview request data
 * @returns Promise<PaymentPreviewResponse> - Payment preview data
 */
export const getPaymentPreview = async (
  tournamentId: string,
  teamId: string,
  requestData: PaymentPreviewRequest
): Promise<PaymentPreviewResponse> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    console.log('🔍 Payment Service - API URL:', `${API_BASE_URL}/api/v1/tournament-registration/payment-preview/${tournamentId}/${teamId}`);
    console.log('🔍 Payment Service - Request data:', requestData);

    const response = await secureApiWithCsrf.post<PaymentPreviewResponse>(
      `/api/v1/tournament-registration/payment-preview/${tournamentId}/${teamId}`,
      requestData
    );

    console.log('🔍 Payment Service - Response data validation:', {
      hasData: !!response.data,
      totalAmount: response.data?.totalAmount,
      fullAmount: response.data?.fullAmount,
      perPlayerFee: response.data?.perPlayerFee,
      playerCount: response.data?.playerCount,
      amountPaid: response.data?.amountPaid,
      minimumAmountToPay: response.data?.minimumAmountToPay,
      amountToPay: response.data?.amountToPay,
      balanceAmount: response.data?.balanceAmount
    });
    
    return response;
  } catch (error: any) {
    console.error('Error fetching payment preview:', error);
    console.error('Error response:', error.response);
    console.error('Error status:', error.response?.status);
    console.error('Error data:', error.response?.data);
    
    // Handle different types of errors
    if (error.response?.status === 403) {
      throw new Error('Access denied (403). Please check your permissions or contact support.');
    } else if (error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to fetch payment preview');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to fetch payment preview');
    }
  }
};



/**
 * Add players mid-season for teams with existing registration
 * @param registrationId - The existing registration ID
 * @param newPlayers - Array of new player IDs to be added
 * @param amountPaid - The amount being paid
 * @param paymentType - The type of payment ('full_amount', 'min_amount', or 'custom_amount')
 * @returns Promise<any> - Response data
 */
export const addPlayersMidSeason = async (
  registrationId: string,
  newPlayers: string[],
  amountPaid: number,
  paymentType: 'full_amount' | 'min_amount' | 'custom_amount',
  paymentMode: 'online' | 'offline' = 'online'
): Promise<any> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    console.log('🔍 Payment Service - Add Players Mid-Season API URL:', `${API_BASE_URL}/api/v1/tournament-registration/add-players-mid-season/${registrationId}`);
    console.log('🔍 Payment Service - Add Players Mid-Season Request data:', { 
      newPlayers, 
      amountPaid, 
      paymentType 
    });

    const response = await secureApiWithCsrf.post(
      `/api/v1/tournament-registration/add-players-mid-season/${registrationId}`,
      { 
        newPlayers, 
        amountPaid, 
        paymentType,
        paymentMode
      }
    );

    return response;
  } catch (error: any) {
    console.error('Error adding players mid-season:', error);
    console.error('Error response:', error.response);
    console.error('Error status:', error.response?.status);
    console.error('Error data:', error.response?.data);
    
    // Handle different types of errors
    if (error.response?.status === 403) {
      throw new Error('Access denied (403). Please check your permissions or contact support.');
    } else if (error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to add players mid-season');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to add players mid-season');
    }
  }
};

/**
 * Get milestone payments for an existing registration
 * @param registrationId - The existing registration ID
 * @returns Promise<any> - Response data containing milestone payments
 */
export const getMilestonePaymentsForRegistration = async (
  registrationId: string
): Promise<any> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    console.log('🔍 Payment Service - Get Milestone Payments API URL:', `${API_BASE_URL}/api/v1/tournament-registration/milestone-payments/${registrationId}`);

    const response = await secureApiWithCsrf.get(
      `/api/v1/tournament-registration/milestone-payments/${registrationId}`
    );

    return response;
  } catch (error: any) {
    console.error('Error getting milestone payments:', error);
    console.error('Error response:', error.response);
    console.error('Error status:', error.response?.status);
    console.error('Error data:', error.response?.data);
    
    // Handle different types of errors
    if (error.response?.status === 403) {
      throw new Error('Access denied (403). Please check your permissions or contact support.');
    } else if (error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to get milestone payments');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to get milestone payments');
    }
  }
};

/**
 * Create payment order for tournament registration
 * @param tournamentId - The tournament ID
 * @param teamId - The team ID
 * @param amount - Payment amount
 * @param playerIds - Array of player IDs
 * @returns Promise<any> - Payment order data
 */
export const createPaymentOrder = async (
  tournamentId: string,
  teamId: string,
  amount: number,
  playerIds: string[]
): Promise<any> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    const response = await secureApiWithCsrf.post(
      `/api/v1/tournament-registration/create-payment-order`,
      {
        tournamentId,
        teamId,
        amount,
        playerIds
      }
    );

    return response;
  } catch (error: any) {
    console.error('Error creating payment order:', error);
    
    if (error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to create payment order');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to create payment order');
    }
  }
};

// =========================
// Tournament Registration API
// =========================

/**
 * Add tournament registration
 * @param tournamentId - The tournament ID
 * @param teamId - The team ID
 * @param requestData - Registration request data
 * @returns Promise<AddRegistrationResponse> - Registration response data
 */
export const addTournamentRegistration = async (
  tournamentId: string,
  teamId: string,
  requestData: AddRegistrationRequest
): Promise<AddRegistrationResponse> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    console.log('🔍 Add Registration - API URL:', `${API_BASE_URL}/api/v1/tournament-registration/add-registration/${tournamentId}/${teamId}`);
    console.log('🔍 Add Registration - Request data:', requestData);

    const response = await secureApiWithCsrf.post<AddRegistrationResponse>(
      `/api/v1/tournament-registration/add-registration/${tournamentId}/${teamId}`,
      requestData
    );

    return response;
  } catch (error: any) {
    console.error('Error adding tournament registration:', error);
    console.error('Error response:', error.response);
    console.error('Error status:', error.response?.status);
    console.error('Error data:', error.response?.data);
    
    if (error.response?.status === 403) {
      throw new Error('Access denied (403). Please check your permissions or contact support.');
    } else if (error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to add tournament registration');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to add tournament registration');
    }
  }
};

// =========================
// Tournament Payment Order API
// =========================

/**
 * Create tournament payment order for Razorpay
 * @param requestData - Payment order request data
 * @returns Promise<CreateTournamentOrderResponse> - Payment order response data
 */
export const createTournamentPaymentOrder = async (
  requestData: CreateTournamentOrderRequest
): Promise<CreateTournamentOrderResponse> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    console.log('🔍 Create Tournament Order - API URL:', `${API_BASE_URL}/api/v1/payments/create-order-tournament`);
    console.log('🔍 Create Tournament Order - Request data:', requestData);

    const response = await secureApiWithCsrf.post<CreateTournamentOrderResponse>(
      `/api/v1/payments/create-order-tournament`,
      requestData
    );

    return response;
  } catch (error: any) {
    console.error('Error creating tournament payment order:', error);
    console.error('Error response:', error.response);
    console.error('Error status:', error.response?.status);
    console.error('Error data:', error.response?.data);
    
    if (error.response?.status === 403) {
      throw new Error('Access denied (403). Please check your permissions or contact support.');
    } else if (error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to create payment order');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to create payment order');
    }
  }
}; 